import { makeStyles } from '@material-ui/core';
import typography from '../../../theme/typography';
import { pluralize } from 'inflection';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import clsx from 'clsx';
import { Button } from '../Button';
import { SEARCH_ACTION_BUTTON_TEXT } from '../../../assets/copy';
import { handleSearchSelect } from '../../search/helpers';
import { goToSearchPage } from './useCuisineAndRestaurantSearch';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';

const useStyles = makeStyles((theme) => ({
  listbox: {
    width: theme.spacing(45),
    boxSizing: 'border-box',
    margin: 0,
    padding: theme.spacing(3, 0),
    zIndex: 10,
    position: 'absolute',
    listStyle: 'none',
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    maxHeight: 300,
    borderRadius: 6,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  listgroup: {
    margin: 0,
    padding: 0,
  },
  option: {
    ...typography.small2Normal,
    margin: 0,
    padding: theme.spacing(0.5, 3),
    listStyle: 'none',
    '&[data-focus=true]': {
      color: theme.palette.secondary[65],
      cursor: 'pointer',
    },
    '&:active': {
      color: theme.palette.secondary[80],
    },
  },
  groupHeading: {
    ...typography.medium1Semibold,
    textTransform: 'capitalize',
    padding: theme.spacing(0, 3, 1),
  },
  bold: {
    fontWeight: 600,
  },
  searchButtonContainer: {
    '& button': {
      width: '-webkit-fill-available',
      margin: theme.spacing(0, 3),
    },
  },
}));

/**
 * Autocomplete component for use with @material-ui/lab useAutocomplete hook
 *
 * @param {object} props
 * @param {{key: number, index: number, group: string, options: {name: string, type: string}[]}[]} props.groupedOptions
 * @param {function} props.getListboxProps `getListboxProps` function from useAutocomplete hook
 * @param {function} props.getOptionProps `getOptionProps` function from useAutocomplete hook
 * @param {function} [props.getInputProps] `getInputProps` function from useAutocomplete hook
 * @returns {JSX.Element}
 */
export const Autocomplete = ({
  groupedOptions,
  getListboxProps,
  getOptionProps,
  getInputProps,
  showSearchButton = false,
  classes: classesOverride,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { value: inputValue, 'aria-controls': ariaControls } = getInputProps();
  const classes = useStyles({ classes: classesOverride });

  const isInputFocused = useMemo(() => {
    return ariaControls !== null;
  }, [ariaControls]);
  return (
    <>
      {(groupedOptions.length > 0 || isInputFocused) && (
        <ul className={classes.listbox} {...getListboxProps()}>
          {groupedOptions.map((optionGroup, index) => (
            <li key={`group-${index}`}>
              <div className={classes.groupHeading}>
                {pluralize(optionGroup.group)}
              </div>
              <ul className={classes.listgroup}>
                {optionGroup.options.map((option, index) => {
                  const matches = match(option.name, inputValue, {
                    findAllOccurrences: true,
                  });
                  const parts = parse(option.name, matches);
                  return (
                    <li
                      className={classes.option}
                      {...getOptionProps({
                        option,
                        index: index + optionGroup.key,
                      })}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          className={clsx({
                            [classes.bold]: part.highlight,
                          })}>
                          {part.text}
                        </span>
                      ))}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {showSearchButton && (
            <li className={classes.searchButtonContainer}>
              <Button
                onClick={() => {
                  handleSearchSelect(inputValue, dispatch, true);
                  goToSearchPage(history);
                  document.activeElement.blur();
                }}>
                {SEARCH_ACTION_BUTTON_TEXT}
              </Button>
            </li>
          )}
        </ul>
      )}
    </>
  );
};
